import { useState, useEffect } from 'react';
import { useExpenseApi } from './useExpenseApi';
import { useAuth } from '../../contexts/AuthContext';
import { toastNotify } from '../../utils/toastNotify';
import { useDatabasesQuery } from '../../hooks/useDatabasesQuery';
import { useNotionUsersQuery } from '../../hooks/useNotionUsersQuery';

export const useExpenseForm = ({ onAdd, onSuccess }) => {
  const { createExpense } = useExpenseApi();
  const { user } = useAuth();
  const { data: databases = [], isLoading: loadingDatabases } = useDatabasesQuery();
  const { data: notionOptions = [], isLoading: loadingNotionOptions } = useNotionUsersQuery();
  
  const [formData, setFormData] = useState({
    category: '',
    amount: '',
    comment: '',
    executor: '',
    databaseId: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const categories = [
    'аккаунт',
    'работы по дизайну',
    'комюнити',
    'Артдир',
    'редактор',
    'проджект',
    'дизайнер',
    'управленческий учёт',
    'Телеграм-бот',
    'сейлз',
    'артдир аскона',
    'Я работы по дизайну',
    'монтаж видео.',
    'работы по дизайну',
    'Прочие расходы',
    'Коммунальные платежи',
    'Зарплата',
    'Другое'
  ];

  // Set first database as default if available
  useEffect(() => {
    if (databases.length > 0 && !formData.databaseId) {
      setFormData(prev => ({
        ...prev,
        databaseId: databases[0].id
      }));
    }
  }, [databases, formData.databaseId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    if (!formData.category) {
      toastNotify('Укажите статью расходов', { type: 'error', duration: 3000 });
      return;
    }
    if (!formData.executor) {
      toastNotify('Укажите исполнителя', { type: 'error', duration: 3000 });
      return;
    }
    if (!formData.amount) {
      toastNotify('Укажите сумму', { type: 'error', duration: 3000 });
      return;
    }
    const amountNum = Number(formData.amount);
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      toastNotify('Сумма должна быть положительным числом', { type: 'error', duration: 3000 });
      return;
    }
    if (!formData.databaseId) {
      toastNotify('Выберите базу данных', { type: 'error', duration: 3000 });
      return;
    }

    // Формируем строку "Кто вносил" из данных пользователя
    let whoEntered = '';
    if (user) {
      if (user.firstName || user.lastName) {
        whoEntered = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
      } else if (user.username) {
        whoEntered = user.username;
      }
    }

    const expenseData = {
      whatFor: formData.category,
      executor: formData.executor,
      amount: parseFloat(formData.amount),
      text: formData.comment?.trim() || undefined,
      whoEntered: whoEntered || undefined,
      databaseId: formData.databaseId
    };

    try {
      setIsSubmitting(true);
      const result = await createExpense(expenseData);

      if (result.success) {
        onAdd(result.data);
        if (onSuccess) onSuccess();
        toastNotify('Запись успешно добавлена', { type: 'success', duration: 3000 });
        
        // Reset form (keep databaseId)
        setFormData({
          category: '',
          amount: '',
          comment: '',
          executor: '',
          databaseId: formData.databaseId
        });
      } else {
        console.error('Error adding expense:', result.message);
        toastNotify('Ошибка при добавлении расхода: ' + result.message, { type: 'error', duration: 4000 });
      }
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = error.response?.data?.message || error.message;
      toastNotify('Ошибка при отправке данных на сервер: ' + errorMessage, { type: 'error', duration: 4000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for databaseId - need to find ID by name
    if (name === 'databaseId') {
      const selectedDb = databases.find(db => db.name === value);
      setFormData(prev => ({
        ...prev,
        [name]: selectedDb ? selectedDb.id : value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Get database name by ID for display in CustomSelect
  const getDatabaseDisplayValue = () => {
    if (!formData.databaseId) return '';
    const db = databases.find(db => db.id === formData.databaseId);
    return db ? db.name : '';
  };

  return {
    formData,
    isSubmitting,
    categories,
    notionOptions,
    loadingNotionOptions,
    databases,
    loadingDatabases,
    databaseDisplayValue: getDatabaseDisplayValue(),
    handleSubmit,
    handleInputChange
  };
};

